/**
 * Connectors API — installed list + sync trigger.
 *
 * Sprint C' only ships the read + sync surface; install/uninstall UI
 * comes when the connector marketplace lands. For now operators
 * provision installations directly in DB or via curl.
 */

import { api, type RequestOptions } from "./client";


export interface InstallationDto {
  id:                       string;
  connector_name:           string;
  connector_version:        string;
  enabled:                  boolean;
  last_health_status:       string | null;
  /** Sprint AR — names of credential fields that have been filled.
   *  Drives the UI's "configured" / "missing" indicators without
   *  ever exposing the values. */
  configured_field_names:   string[];
}


export interface RequiredEnvDto {
  name:        string;
  description: string | null;
  secret:      boolean;
}


export interface AvailableConnectorDto {
  name:         string;
  version:      string;
  description:  string;
  runtime:      string;
  homepage:     string | null;
  capabilities: {
    name:                    string;
    summary:                 string;
    side_effects:            boolean;
    requires_human_approval: boolean;
  }[];
  required_env: RequiredEnvDto[];
}


export interface InstallRequest {
  connector_name: string;
  config:         Record<string, string>;
  enabled?:       boolean;
}


export interface SyncAcceptedDto {
  correlation_id: string;
  event_id:       string;
}


export interface SyncRunDto {
  id:             string;
  source:         string;
  status:         "running" | "succeeded" | "failed";
  started_at:     string;
  finished_at:    string | null;
  duration_s:     number | null;
  dry_run:        boolean;
  created:        number;
  updated:        number;
  unchanged:      number;
  removed:        number;
  errors:         number;
  error_summary:  string | null;
  correlation_id: string | null;
}


/** Sprint M-test — synchronous probe result. The server's
 *  per-connector probe fires ONE minimal authenticated call
 *  against the upstream and reports back. `detail` is free-form
 *  diagnostics (sample row count, connected account name); the
 *  UI shows it under the green/red status line. */
export interface ConnectorTestResult {
  ok:      boolean;
  message: string;
  detail:  Record<string, unknown>;
}


/** Sprint F4 — health rollup per installed connector.
 *  Aggregated from the last 10 sync_runs rows. */
export interface ConnectorHealthDto {
  connector_name:           string;
  last_successful_call_at:  string | null;
  last_attempted_call_at:   string | null;
  last_status:              string | null;
  consecutive_failures:     number;
  recent_errors_summary:    string[];
  is_healthy:               boolean;
  stale:                    boolean;
}


export const connectorsApi = {
  listInstalled(opts?: RequestOptions): Promise<InstallationDto[]> {
    return api.get<InstallationDto[]>("/connectors/installed", opts);
  },

  /** Sprint F4 — fetch the health rollup for every installed
   *  connector in this tenant. One round-trip; UI keys results
   *  by `connector_name` when correlating with /installed. */
  health(opts?: RequestOptions): Promise<ConnectorHealthDto[]> {
    return api.get<ConnectorHealthDto[]>("/connectors/health", opts);
  },

  /** Available connectors registered on this OS instance —
   *  drives the Settings page's "what can I install?" list. */
  listAvailable(opts?: RequestOptions): Promise<AvailableConnectorDto[]> {
    return api.get<AvailableConnectorDto[]>("/connectors/available", opts);
  },

  /** Upsert credentials for a connector. Empty-string values clear
   *  the corresponding field; absent keys preserve existing values. */
  install(
    body: InstallRequest,
    opts?: RequestOptions,
  ): Promise<InstallationDto> {
    return api.post<InstallationDto>("/connectors/install", body, opts);
  },

  /** Hard-delete the tenant's installation. Operators use this to
   *  fully reset credentials. */
  uninstall(
    connectorName: string,
    opts?: RequestOptions,
  ): Promise<void> {
    return api.delete<void>(`/connectors/install/${connectorName}`, opts);
  },

  /** Sprint M-test — verify saved credentials against the upstream
   *  API. One synchronous round-trip → `{ok, message, detail}`. UI
   *  uses this to give operators an instant green/red signal after
   *  they paste a key, before they discover the failure via a
   *  failed sync hours later. */
  testConnection(
    connectorName: string,
    opts?: RequestOptions,
  ): Promise<ConnectorTestResult> {
    return api.post<ConnectorTestResult>(
      `/connectors/${connectorName}/test`, {}, opts,
    );
  },

  /**
   * Trigger a sync for the named connector. Fires `sync.requested` —
   * the bridge agent picks it up and runs one cycle.
   */
  sync(
    connectorName: string,
    body: { since?: string; dry_run?: boolean; page_size?: number } = {},
    opts?: RequestOptions,
  ): Promise<SyncAcceptedDto> {
    return api.post<SyncAcceptedDto>(
      `/connectors/${connectorName}/sync`, body, opts,
    );
  },

  /**
   * Recent sync runs — Sprint O. Filter by source to scope to one
   * connector, omit for a global run history. Newest-first.
   */
  syncRuns(
    params: { source?: string; limit?: number } = {},
    opts?: RequestOptions,
  ): Promise<SyncRunDto[]> {
    const q = new URLSearchParams();
    if (params.source) q.set("source", params.source);
    q.set("limit", String(params.limit ?? 20));
    return api.get<SyncRunDto[]>(`/sync-runs?${q.toString()}`, opts);
  },
};
